import { useEffect, useState } from "react";
import { isStandalone } from "../lib/registerSW.js";

const DISMISS_KEY = "pwa.install.dismissed_at";
const DISMISS_TTL_DAYS = 14;

function recentlyDismissed() {
  try {
    const ts = parseInt(localStorage.getItem(DISMISS_KEY) || "0", 10);
    if (!ts) return false;
    return Date.now() - ts < DISMISS_TTL_DAYS * 24 * 60 * 60 * 1000;
  } catch { return false; }
}

function markDismissed() {
  try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch {}
}

function isIOSSafari() {
  const ua = navigator.userAgent;
  const isIOS = /iPad|iPhone|iPod/.test(ua);
  const isSafari = /^((?!chrome|android|crios|fxios).)*safari/i.test(ua);
  return isIOS && isSafari;
}

export function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [showAndroid, setShowAndroid] = useState(false);
  const [showIOS, setShowIOS] = useState(false);

  useEffect(() => {
    if (isStandalone() || recentlyDismissed()) return;

    // Android/Chrome: beforeinstallprompt fires
    const handleBIP = (e) => {
      e.preventDefault();
      setDeferred(e);
      setShowAndroid(true);
    };
    window.addEventListener("beforeinstallprompt", handleBIP);

    // iOS Safari: no beforeinstallprompt, show manual hint
    let iosTimer = null;
    if (isIOSSafari()) {
      iosTimer = setTimeout(() => setShowIOS(true), 8000);  // wait 8s zodat user content ziet
    }

    // Hide when installed
    const handleInstalled = () => {
      setShowAndroid(false);
      setShowIOS(false);
      setDeferred(null);
    };
    window.addEventListener("appinstalled", handleInstalled);

    return () => {
      window.removeEventListener("beforeinstallprompt", handleBIP);
      window.removeEventListener("appinstalled", handleInstalled);
      if (iosTimer) clearTimeout(iosTimer);
    };
  }, []);

  if (showAndroid && deferred) {
    return (
      <div className="pwa-install-banner" role="dialog" aria-label="Installeer als app">
        <div className="pwa-install-icon">📱</div>
        <div className="pwa-install-text">
          <b>Installeer als app</b>
          <span>Snellere toegang en werkt offline</span>
        </div>
        <button
          className="pwa-install-btn pwa-install-btn-primary"
          onClick={async () => {
            try {
              await deferred.prompt();
              const choice = await deferred.userChoice;
              if (choice.outcome === "dismissed") markDismissed();
            } catch {}
            setDeferred(null);
            setShowAndroid(false);
          }}
        >
          Installeer
        </button>
        <button
          className="pwa-install-btn pwa-install-btn-secondary"
          onClick={() => { markDismissed(); setShowAndroid(false); }}
          aria-label="Sluiten"
        >×</button>
      </div>
    );
  }

  if (showIOS) {
    return (
      <div className="pwa-install-banner pwa-install-banner-ios" role="dialog" aria-label="Installeer op iOS">
        <div className="pwa-install-icon">🏝️</div>
        <div className="pwa-install-text">
          <b>Op je startscherm zetten?</b>
          <span>Tik <span className="ios-share-icon" aria-label="deel">⎙</span> en kies "Zet op beginscherm"</span>
        </div>
        <button
          className="pwa-install-btn pwa-install-btn-secondary"
          onClick={() => { markDismissed(); setShowIOS(false); }}
          aria-label="Sluiten"
        >×</button>
      </div>
    );
  }

  return null;
}
